/**
 * <relic-sticky-atc> — mobile sticky add-to-cart bar.
 *
 * Shows when the main ATC sentinel scrolls above the viewport and hides when
 * the user scrolls back to it or when a footer-zone marker enters view.
 *
 * Required descendants:
 *   [data-relic-atc-sentinel] — on the main ATC button, within the same section
 *
 * Required elsewhere:
 *   [data-relic-sticky-atc-hide] — intentional hide boundary (e.g. site footer).
 *   Only add to sections that should dismiss the sticky bar when scrolled into view.
 */

const DESKTOP_MQ = '(min-width: 1000px)';

if (!customElements.get('relic-sticky-atc')) {
  class RelicStickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (!this.section) return;

      this.sentinel = this.section.querySelector('[data-relic-atc-sentinel]');
      if (!this.sentinel) return;

      this.hideBoundaryIntersecting = new Set();
      this.observedHideBoundaries = new WeakSet();
      this.pastSentinel = false;

      this.stickyButton = this.querySelector('[data-relic-atc-sticky]');
      this.productForm = this.section.querySelector(
        'product-form form[action*="/cart/add"]'
      );

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (this.stickyButton && this.productForm) {
        this.stickyButton.addEventListener(
          'click',
          () => {
            if (this.stickyButton.disabled) return;
            this.productForm.requestSubmit();
          },
          { signal }
        );
      }

      this.desktopMq = window.matchMedia(DESKTOP_MQ);
      this.onDesktopChange = () => this.updateVisibility();
      this.desktopMq.addEventListener('change', this.onDesktopChange);

      this.observer = new IntersectionObserver(
        (entries) => this.onIntersection(entries),
        { threshold: 0 }
      );
      this.observer.observe(this.sentinel);

      this.observeHideBoundaries = this.observeHideBoundaries.bind(this);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.observeHideBoundaries, {
          once: true,
        });
      } else {
        this.observeHideBoundaries();
      }

      this.updateVisibility();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.desktopMq?.removeEventListener('change', this.onDesktopChange);
      document.removeEventListener('DOMContentLoaded', this.observeHideBoundaries);
      this.classList.remove('is-visible');
    }

    observeHideBoundaries() {
      document.querySelectorAll('[data-relic-sticky-atc-hide]').forEach((boundary) => {
        if (this.observedHideBoundaries.has(boundary)) return;
        this.observedHideBoundaries.add(boundary);
        this.observer.observe(boundary);
      });
    }

    onIntersection(entries) {
      for (const entry of entries) {
        if (entry.target === this.sentinel) {
          this.pastSentinel =
            !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        }

        if (entry.target.hasAttribute('data-relic-sticky-atc-hide')) {
          if (entry.isIntersecting) {
            this.hideBoundaryIntersecting.add(entry.target);
          } else {
            this.hideBoundaryIntersecting.delete(entry.target);
          }
        }
      }

      this.updateVisibility();
    }

    updateVisibility() {
      const hideBoundaryNotVisible = this.hideBoundaryIntersecting.size === 0;
      const show =
        !this.desktopMq.matches && this.pastSentinel && hideBoundaryNotVisible;
      this.classList.toggle('is-visible', show);
    }
  }

  customElements.define('relic-sticky-atc', RelicStickyAtc);
}
